/**
 * Admin UI Story Data Access
 *
 * Stories use a split-table architecture:
 * - `stories` table: Core story data (brief, slug, tags, image, show reference)
 * - `story_translations` table: Per-language content (title, script, phase, audio metadata)
 *
 * Reads use the `stories_admin_enriched` view which joins both tables.
 * Writes must target the correct table based on field type.
 */
import type {
  Story,
  StoryCore,
  StoryTranslation,
  StoryWithShow,
  StoryWithShowWithGenre,
  StoryListItem,
} from '@musedrops/shared';
import {
  fromDbKeys,
  toDbKeys,
  snakeCase,
  logger,
  generateSlugFromTitle,
  withUpdateTimestamp,
  withCreateTimestamp,
} from '@musedrops/shared';
import { supabase } from '../services/supabase';
import { isUuid, orIlikeContains } from '../utils/postgrest';

// Lifecycle processing is handled automatically by the on-data-change Edge Function
// which receives database triggers on INSERT/UPDATE/DELETE.

/**
 * Fields that live on story_translations table.
 * All other Story fields live on the stories table.
 */
const TRANSLATION_FIELDS = new Set([
  // User-facing content
  'title',
  'subtitle',
  'description',
  'script',
  'searchTerms',
  // Audio metadata
  'durationSec',
  'fileSizeBytes',
  'durationSecExternal',
  'fileSizeBytesExternal',
  'voiceAudioUpdatedAt',
  'mixedAudioUpdatedAt',
  'externalAudioUpdatedAt',
  // Lifecycle state
  'phase',
  'error',
  'lang',
  // Generation metadata (per-translation)
  'scriptPresetUsed',
  'ttsPresetUsed',
  'usedDebug',
  // Pre-publish validation
  'prePublishCheckedAt',
  'prePublishIssues',
  // Issue #767: disabled state is per-translation
  'enabled',
  'disabledAt',
  'disabledReason',
  // Search
  'searchVector',
]);

export interface ListStoriesOptions {
  page?: number;
  perPage?: number;
  sortField?: keyof Story | string;
  sortOrder?: 'ASC' | 'DESC';
  filter?: Record<string, unknown>;
  withTotal?: boolean;
}

export async function listStories(
  options: ListStoriesOptions = {}
): Promise<{ data: StoryWithShow[]; total?: number }> {
  const {
    page = 1,
    perPage,
    sortField,
    sortOrder = 'ASC',
    filter: originalFilter,
    withTotal = false,
  } = options;

  const selectOptions = withTotal ? { count: 'exact' as const } : undefined;

  // Use enriched view to get combined story + translation data
  let query = supabase
    .from('stories_admin_enriched')
    .select('*', selectOptions);

  if (typeof perPage === 'number') {
    const start = (page - 1) * perPage;
    const end = start + perPage - 1;
    query = query.range(start, end);
  }

  // Extract search query before toDbKeys conversion
  let searchQuery: string | undefined;
  let filter = originalFilter;

  if (filter?.q && typeof filter.q === 'string') {
    searchQuery = filter.q;
    // Remove q from filter object before toDbKeys
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { q: _q, ...restFilter } = filter as {
      q: string;
      [key: string]: unknown;
    };
    filter = restFilter;
  }

  const normalizedFilter = filter ? toDbKeys(filter) : undefined;
  if (normalizedFilter) {
    Object.entries(normalizedFilter).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        query = query.in(key, value as unknown[]);
        return;
      }
      query = query.eq(key, value as string | number | boolean);
    });
  }

  // Apply text search across title, brief, and search_terms
  if (searchQuery) {
    query = query.or(
      orIlikeContains(['title', 'brief', 'search_terms'], searchQuery)
    );
  }

  if (sortField) {
    query = query.order(snakeCase(String(sortField)), {
      ascending: sortOrder === 'ASC',
    });
  } else {
    query = query.order('updated_at', { ascending: false });
  }

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Failed to fetch stories: ${error.message}`);
  }

  return {
    data: fromDbKeys<StoryWithShow[]>(
      (data ?? []) as Record<string, unknown>[]
    ),
    total: withTotal ? (count ?? 0) : undefined,
  };
}

/**
 * List stories with aggregated translations for admin matrix view.
 * Uses stories_with_trans view which returns one row per story with translations as JSONB.
 */
export interface ListStoriesWithTransOptions {
  page?: number;
  perPage?: number;
  sortField?: string;
  sortOrder?: 'ASC' | 'DESC';
  filter?: Record<string, unknown>;
  withTotal?: boolean;
}

export async function listStoriesWithTrans(
  options: ListStoriesWithTransOptions = {}
): Promise<{ data: StoryListItem[]; total?: number }> {
  const {
    page = 1,
    perPage,
    sortField,
    sortOrder = 'ASC',
    filter: originalFilter,
    withTotal = false,
  } = options;

  const selectOptions = withTotal ? { count: 'exact' as const } : undefined;

  let query = supabase
    .from('stories_with_trans')
    .select('*', selectOptions);

  if (typeof perPage === 'number') {
    const start = (page - 1) * perPage;
    const end = start + perPage - 1;
    query = query.range(start, end);
  }

  // Extract search query before toDbKeys conversion
  let searchQuery: string | undefined;
  let filter = originalFilter;

  if (filter?.q && typeof filter.q === 'string') {
    searchQuery = filter.q;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { q: _q, ...restFilter } = filter as {
      q: string;
      [key: string]: unknown;
    };
    filter = restFilter;
  }

  const normalizedFilter = filter ? toDbKeys(filter) : undefined;
  if (normalizedFilter) {
    Object.entries(normalizedFilter).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') {
        return;
      }
      if (Array.isArray(value)) {
        query = query.in(key, value as unknown[]);
        return;
      }
      query = query.eq(key, value as string | number | boolean);
    });
  }

  // Apply text search across topic and brief
  if (searchQuery) {
    query = query.or(orIlikeContains(['topic', 'brief', 'slug'], searchQuery));
  }

  if (sortField) {
    query = query.order(snakeCase(String(sortField)), {
      ascending: sortOrder === 'ASC',
    });
  } else {
    query = query.order('updated_at', { ascending: false });
  }

  const { data, error, count } = await query;

  if (error) {
    throw new Error(`Failed to fetch stories with translations: ${error.message}`);
  }

  // Transform DB keys to camelCase
  const transformedData = (data ?? []).map(row => {
    const item = fromDbKeys<StoryListItem>(row as Record<string, unknown>);
    // translations column is already JSONB, keep as-is
    return item;
  });

  return {
    data: transformedData,
    total: withTotal ? (count ?? 0) : undefined,
  };
}

export async function createStory(
  story: Partial<Story> & Pick<Story, 'showSlug'>
): Promise<Story> {
  // Generate slug if not provided
  // If title is provided, use it for slug; otherwise use brief or generate random
  const slugSource = story.title || story.brief?.substring(0, 50) || 'story';
  const slug =
    story.slug ||
    `${generateSlugFromTitle(slugSource)}-${Math.floor(10000 + Math.random() * 90000)}`;

  // Split fields - only core story fields go to stories table
  // Translations are created via ensure-story-translations → gen-story-translation pipeline
  // This ensures ALL languages get LLM-generated metadata (title, subtitle, description, keywords)
  const coreStoryData: Record<string, unknown> = {
    slug,
    showSlug: story.showSlug,
  };

  // Only add non-translation fields to core story data
  for (const [key, value] of Object.entries(story)) {
    if (key === 'showSlug' || key === 'slug') continue; // Already handled
    if (!TRANSLATION_FIELDS.has(key)) {
      coreStoryData[key] = value;
    }
    // Translation fields (title, subtitle, description, etc.) are intentionally NOT stored here
    // They will be generated by gen-story-translation for all required languages
  }

  // Insert core story record
  // on-data-change will trigger ensure-story-translations which queues gen-story-translation
  // for all required languages (based on show.lang or languages.include_in_global_gen)
  const { data, error } = await supabase
    .from('stories')
    .insert(withCreateTimestamp(toDbKeys(coreStoryData)))
    .select('*')
    .single();

  if (error) {
    logger.error('Failed to create story', {
      error: error.message,
      code: error.code,
    });
    throw new Error(`Failed to create story: ${error.message}`);
  }

  // Return core story data
  // Note: Translation data will be available after gen-story-translation completes (async)
  // The enriched view will show the story without translation fields until then
  const createdStory = fromDbKeys<Story>(data as Record<string, unknown>);

  // Note: Lifecycle processing is now handled automatically by on-data-change Edge Function
  // which triggers ensure-story-translations → gen-story-translation → story_translations.INSERT → lifecycle

  return createdStory;
}

export async function insertStorySeeds({
  storySeeds,
  showSlug,
  ideaPresetUsed,
}: {
  storySeeds: Partial<Story>[];
  showSlug: string;
  ideaPresetUsed?: string;
}): Promise<void> {
  const filteredSeeds = storySeeds.filter(seed => seed.title && seed.brief);

  if (filteredSeeds.length === 0) {
    return;
  }

  // Prepare core story records (stories table only)
  // Translations are created via ensure-story-translations → gen-story-translation → META_READY
  const storyRecords = filteredSeeds.map(seed => {
    const slug =
      seed.slug ||
      (seed.title
        ? `${generateSlugFromTitle(seed.title)}-${Math.floor(10000 + Math.random() * 90000)}`
        : undefined);
    return withCreateTimestamp(
      toDbKeys({
        slug,
        showSlug,
        brief: seed.brief,
        tags: seed.tags,
        ...(ideaPresetUsed && { ideaPresetUsed }),
      })
    );
  });

  // Insert core story records
  // on-data-change will trigger ensure-story-translations which creates translations
  const { error } = await supabase
    .from('stories')
    .insert(storyRecords)
    .select('id, slug');

  if (error) {
    logger.error('Failed to insert stories', {
      error: error.message,
      code: error.code,
    });
    throw new Error(`Failed to insert stories: ${error.message}`);
  }
}

export async function updateStory(
  story: Partial<Story> & Pick<Story, 'id'>
): Promise<void> {
  // Split fields between stories and story_translations tables
  const coreFields: Record<string, unknown> = {};
  const translationFields: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(story)) {
    if (key === 'id') continue; // Handle separately
    if (TRANSLATION_FIELDS.has(key)) {
      translationFields[key] = value;
    } else {
      coreFields[key] = value;
    }
  }

  // Update core story fields if any
  if (Object.keys(coreFields).length > 0) {
    const { error } = await supabase
      .from('stories')
      .update(withUpdateTimestamp(toDbKeys(coreFields)))
      .eq('id', story.id);

    if (error) {
      throw new Error(`Failed to update story ${story.id}: ${error.message}`);
    }
  }

  // Update translation fields if any
  if (Object.keys(translationFields).length > 0) {
    // Get story's slug and language from show (PK is story_slug + lang)
    const { data: storyData, error: fetchError } = await supabase
      .from('stories')
      .select('slug, show_slug, shows!inner(lang)')
      .eq('id', story.id)
      .single();

    if (fetchError) {
      throw new Error(
        `Failed to get story language for ${story.id}: ${fetchError.message}`
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const lang = (storyData as any).shows?.lang || 'en';
    const storySlug = storyData.slug;

    const { error: translationError } = await supabase
      .from('story_translations')
      .update(withUpdateTimestamp(toDbKeys(translationFields)))
      .eq('story_slug', storySlug)
      .eq('lang', lang);

    if (translationError) {
      throw new Error(
        `Failed to update story translation ${storySlug}/${lang}: ${translationError.message}`
      );
    }
  }
}

export async function getLastStoryTopicsOfShow(
  showSlug: string,
  limit: number = 50
): Promise<string[]> {
  const { data, error } = await supabase
    .from('stories')
    .select('topic')
    .eq('show_slug', showSlug)
    .not('topic', 'is', null)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(
      `Failed to fetch last story topics of show ${showSlug}: ${error.message}`
    );
  }

  return data.map(row => row.topic).filter(Boolean) as string[];
}

export async function listStoriesByShowSlug(
  showSlug: string,
  limit = 5
): Promise<Story[]> {
  // Issue #557: Use enriched view to get combined story + translation data
  // The stories table no longer has title, description, script, phase, etc.
  const { data, error } = await supabase
    .from('stories_admin_enriched')
    .select('*')
    .eq('show_slug', showSlug)
    .order('updated_at', { ascending: false })
    .limit(limit);

  if (error) {
    throw new Error(
      `Failed to fetch stories for show ${showSlug}: ${error.message}`
    );
  }

  return (data ?? []).map(row =>
    fromDbKeys<Story>(row as Record<string, unknown>)
  );
}

export async function getStory(id: string): Promise<Story> {
  // Use enriched view to get combined story + translation data
  const { data, error } = await supabase
    .from('stories_admin_enriched')
    .select('*')
    .eq('id', id)
    .single();

  if (error || !data) {
    throw new Error(`Failed to fetch story ${id}: ${error?.message}`);
  }

  return fromDbKeys(data) as Story;
}

export async function getStoryWithShow(id: string): Promise<StoryWithShow> {
  // Enriched view includes show data via join
  const { data, error } = await supabase
    .from('stories_admin_enriched')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    throw new Error(`Failed to fetch story with show ${id}: ${error.message}`);
  }

  // Transform flat view data back to nested structure for compatibility
  const storyData = fromDbKeys(data as Record<string, unknown>) as Record<
    string,
    unknown
  >;
  return {
    ...storyData,
    show: {
      name: storyData.showName,
      slug: storyData.showSlug,
      lang: storyData.showLang,
      allowStoryImages: storyData.showAllowStoryImages,
    },
  } as StoryWithShow;
}

/**
 * Use enriched view which already joins story_translations, shows, and genres.
 */
export async function getStoryWithShowWithGenre(
  id: string
): Promise<StoryWithShowWithGenre> {
  const { data, error } = await supabase
    .from('stories_admin_enriched')
    .select('*')
    .eq('id', id)
    .single();

  if (error) {
    logger.error('Failed to fetch story with show with genre', {
      storyId: id,
      error: error.message,
    });
    throw new Error(
      `Failed to fetch story with show with genre ${id}: ${error.message}`
    );
  }

  // Transform flat view data back to nested structure for compatibility
  const storyData = fromDbKeys(data as Record<string, unknown>) as Record<
    string,
    unknown
  >;
  return {
    ...storyData,
    show: {
      name: storyData.showName,
      slug: storyData.showSlug,
      lang: storyData.showLang,
      description: storyData.showDescription,
      topic: storyData.showTopic,
      style: storyData.showStyle,
      tags: storyData.showTags,
      audioLogoDesc: storyData.showAudioLogoDesc,
      storyScope: storyData.showStoryScope,
      enabled: storyData.showEnabled,
      imageUpdatedAt: storyData.showImageUpdatedAt,
      introMusicUpdatedAt: storyData.showIntroMusicUpdatedAt,
      outroMusicUpdatedAt: storyData.showOutroMusicUpdatedAt,
      musicUpdatedAt: storyData.showMusicUpdatedAt,
      voiceProfile1: storyData.showVoiceProfile1,
      voiceProfile2: storyData.showVoiceProfile2,
      voiceIndex1: storyData.showVoiceIndex1,
      voiceIndex2: storyData.showVoiceIndex2,
      voiceInstructions1: storyData.showVoiceInstructions1,
      voiceInstructions2: storyData.showVoiceInstructions2,
      hostPersona1: storyData.showHostPersona1,
      hostPersona2: storyData.showHostPersona2,
      scriptInstructions1: storyData.showScriptInstructions1,
      scriptInstructions2: storyData.showScriptInstructions2,
      mixingSpec: storyData.showMixingSpec,
      dynamicsKey: storyData.showDynamicsKey,
      storyScriptPrompt: storyData.showStoryScriptPrompt,
      genreId: storyData.genreId,
      genre: storyData.genreName
        ? {
            name: storyData.genreName,
            instructions: storyData.genreInstructions,
          }
        : null,
    },
  } as StoryWithShowWithGenre;
}

/**
 * Use enriched view for translation fields.
 */
export async function getStoriesByShowSlugPaginated(
  showSlug: string,
  page = 1,
  perPage = 25
): Promise<{ data: Story[]; total: number }> {
  const start = (page - 1) * perPage;
  const end = start + perPage - 1;

  const { data, error, count } = await supabase
    .from('stories_admin_enriched')
    .select('*', { count: 'exact' })
    .eq('show_slug', showSlug.toLowerCase())
    .order('updated_at', { ascending: false })
    .range(start, end);

  if (error) {
    throw new Error(
      `Failed to fetch stories for show ${showSlug}: ${error.message}`
    );
  }

  return {
    data: (data ?? []).map(row =>
      fromDbKeys<Story>(row as Record<string, unknown>)
    ),
    total: count || 0,
  };
}

export async function bulkDeleteStories(storyIds: string[]): Promise<void> {
  const { error } = await supabase.from('stories').delete().in('id', storyIds);

  if (error) {
    throw new Error(`Failed to bulk delete stories: ${error.message}`);
  }
}

export async function checkShowHasStories(showSlug: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('stories')
    .select('id')
    .eq('show_slug', showSlug.toLowerCase())
    .limit(1);

  if (error) {
    throw new Error(
      `Failed to check stories for show ${showSlug}: ${error.message}`
    );
  }

  return (data?.length || 0) > 0;
}

/**
 * Split-table architecture - create stories only.
 * Translations are created via ensure-story-translations → gen-story-translation → META_READY.
 */
export async function bulkCreateStories(
  stories: Array<{
    title: string;
    brief: string;
    showSlug: string;
    topic?: string;
    takeaway?: string;
  }>
): Promise<Story[]> {
  // Prepare stories with IDs
  const storiesWithIds = stories.map(story => {
    const id = crypto.randomUUID();
    const normalizedShowSlug = story.showSlug.toLowerCase();
    return {
      ...story,
      id,
      showSlug: normalizedShowSlug,
    };
  });

  // Create core story records only
  // on-data-change will trigger ensure-story-translations which creates translations
  const storiesData = storiesWithIds.map(story =>
    withCreateTimestamp(
      toDbKeys({
        id: story.id,
        slug: `${generateSlugFromTitle(story.title)}-${Math.floor(10000 + Math.random() * 90000)}`,
        brief: story.brief,
        ...(story.topic && { topic: story.topic }),
        ...(story.takeaway && { takeaway: story.takeaway }),
        showSlug: story.showSlug,
      })
    )
  );

  const { data, error } = await supabase
    .from('stories')
    .insert(storiesData)
    .select('*');

  if (error) {
    logger.error('Bulk story creation error', {
      error: error.message,
      code: error.code,
    });
    throw new Error(`Failed to bulk create stories: ${error.message}`);
  }

  // Fetch from enriched view to get combined data
  // Note: Translation data may not be immediately available - created async by ensure-story-translations
  const { data: enrichedData, error: fetchError } = await supabase
    .from('stories_admin_enriched')
    .select('*')
    .in(
      'id',
      storiesWithIds.map(s => s.id)
    );

  if (fetchError) {
    logger.warn('Failed to fetch enriched stories after bulk create', {
      error: fetchError.message,
    });
    // Return basic data even if enriched fetch fails
    return (data ?? []).map(row =>
      fromDbKeys<Story>(row as Record<string, unknown>)
    );
  }

  // Note: Lifecycle processing is now handled automatically by on-data-change Edge Function

  return (enrichedData ?? []).map(row =>
    fromDbKeys<Story>(row as Record<string, unknown>)
  );
}

/**
 * Delete all stories for a given show (cascade delete)
 * Returns the count of deleted stories
 */
export async function deleteStoriesByShowSlug(
  showSlug: string
): Promise<number> {
  // First count stories to return the count
  const { count: storyCount } = await supabase
    .from('stories')
    .select('id', { count: 'exact', head: true })
    .eq('show_slug', showSlug);

  if (storyCount === 0 || storyCount === null) {
    return 0;
  }

  // Then delete them
  const { error } = await supabase
    .from('stories')
    .delete()
    .eq('show_slug', showSlug);

  if (error) {
    throw new Error(
      `Failed to delete stories for show ${showSlug}: ${error.message}`
    );
  }

  return storyCount;
}

// ============================================================================
// STORY TRANSLATION OPERATIONS
// ============================================================================

/**
 * Get all translations for a story.
 * Returns translations keyed by language code.
 * Uses storySlug as primary key.
 */
export async function getStoryTranslations(
  storySlug: string
): Promise<Record<string, StoryTranslation>> {
  const { data, error } = await supabase
    .from('story_translations')
    .select('*')
    .eq('story_slug', storySlug);

  if (error) {
    throw new Error(
      `Failed to fetch translations for story ${storySlug}: ${error.message}`
    );
  }

  // Convert to object keyed by language
  const translations: Record<string, StoryTranslation> = {};
  for (const row of data ?? []) {
    const translation = fromDbKeys<StoryTranslation>(
      row as Record<string, unknown>
    );
    translations[translation.lang] = translation;
  }

  return translations;
}

/**
 * Get a single translation for a story + language.
 * Uses storySlug as primary key.
 */
export async function getStoryTranslation(
  storySlug: string,
  lang: string
): Promise<StoryTranslation | null> {
  const { data, error } = await supabase
    .from('story_translations')
    .select('*')
    .eq('story_slug', storySlug)
    .eq('lang', lang)
    .maybeSingle();

  if (error) {
    throw new Error(
      `Failed to fetch translation for story ${storySlug}/${lang}: ${error.message}`
    );
  }

  if (!data) return null;

  return fromDbKeys<StoryTranslation>(data as Record<string, unknown>);
}

/**
 * Update a specific translation for a story + language.
 * Uses storySlug as primary key.
 */
export async function updateStoryTranslation(
  storySlug: string,
  lang: string,
  data: Partial<Omit<StoryTranslation, 'storySlug' | 'lang' | 'createdAt'>>
): Promise<void> {
  const { error } = await supabase
    .from('story_translations')
    .update(withUpdateTimestamp(toDbKeys(data)))
    .eq('story_slug', storySlug)
    .eq('lang', lang);

  if (error) {
    throw new Error(
      `Failed to update translation ${storySlug}/${lang}: ${error.message}`
    );
  }
}

/**
 * Get core story data (without translation fields)
 * Use this when you need just the story entity data.
 * Accepts either a UUID id or a slug.
 */
export async function getStoryCore(idOrSlug: string): Promise<StoryCore | null> {
  // Select all columns — derive hasEmbedding boolean and strip embedding vector before returning
  // Branch on UUID shape: `id` is a uuid column, so filtering it with a slug
  // value would make PostgREST reject the whole query (22P02).
  const { data, error } = await supabase
    .from('stories')
    .select('*')
    .eq(isUuid(idOrSlug) ? 'id' : 'slug', idOrSlug)
    .maybeSingle();

  if (error) {
    throw new Error(`Failed to fetch story core ${idOrSlug}: ${error.message}`);
  }

  if (!data) return null;

  // Derive hasEmbedding boolean and strip the full vector before converting
  const record = data as Record<string, unknown>;
  const hasEmbedding = record.embedding != null && record.embedding !== '';
  delete record.embedding;
  const core = fromDbKeys<StoryCore>(record);
  core.hasEmbedding = hasEmbedding;
  return core;
}

/**
 * Update core story data (without translation fields)
 */
export async function updateStoryCore(
  storyId: string,
  data: Partial<Omit<StoryCore, 'id' | 'updatedAt'>>
): Promise<void> {
  const { error } = await supabase
    .from('stories')
    .update(withUpdateTimestamp(toDbKeys(data)))
    .eq('id', storyId);

  if (error) {
    throw new Error(`Failed to update story core ${storyId}: ${error.message}`);
  }
}
